import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Minus, Plus, Users, Loader2, ArrowRight } from 'lucide-react';
import { apiFetch } from '../services/api';

const API = process.env.REACT_APP_BACKEND_URL;
const PRICE_PER_LICENSE = 10.9;
const fmt = (n) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

export const LicenseUpgradeModal = ({ open, onOpenChange, seatsUsed = 0, seatsTotal = 0 }) => {
  const [add, setAdd] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const startUpgrade = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`${API}/api/digistore/license-upgrade?add=${add}`);
      const data = await res.json();
      if (!res.ok || !data.checkout_url) throw new Error(data.detail || 'Upgrade-Link nicht verfügbar');
      sessionStorage.setItem('sv_license_expected', String(data.new_total));
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-gray-950 border border-yellow-500/40 text-white" data-testid="license-upgrade-modal">
        <DialogHeader>
          <DialogTitle className="text-xl text-yellow-400 flex items-center gap-2">
            <Users className="w-5 h-5" /> Zusätzliche Lizenz benötigt
          </DialogTitle>
          <DialogDescription className="text-gray-300 text-base" data-testid="license-upgrade-text">
            Sie benötigen eine zusätzliche Lizenz für Ihren neuen Mitarbeiter.
            Aktuell sind <strong className="text-white">{seatsUsed} von {seatsTotal}</strong> Lizenzen belegt.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 rounded-xl bg-black/40 border border-gray-800 p-4">
          <p className="text-sm text-gray-400 text-center mb-3">Wie viele Lizenzen hinzubuchen?</p>
          <div className="flex items-center justify-center gap-4">
            <button onClick={() => setAdd(Math.max(1, add - 1))} className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center" data-testid="license-add-minus"><Minus className="w-4 h-4" /></button>
            <span className="text-3xl font-bold w-12 text-center" data-testid="license-add-count">{add}</span>
            <button onClick={() => setAdd(Math.min(200, add + 1))} className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center" data-testid="license-add-plus"><Plus className="w-4 h-4" /></button>
          </div>
          <p className="text-center text-sm text-gray-300 mt-3" data-testid="license-add-summary">
            Neu: <strong className="text-white">{seatsTotal + add} Lizenzen</strong> · +{fmt(add * PRICE_PER_LICENSE)} € / Monat
          </p>
        </div>

        {error && <p className="text-sm text-red-400 text-center" data-testid="license-upgrade-error">{error}</p>}

        <div className="flex gap-3 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800" data-testid="license-upgrade-cancel-btn">
            Später
          </Button>
          <Button onClick={startUpgrade} disabled={loading} className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-black font-bold" data-testid="license-upgrade-confirm-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Lizenz hinzubuchen <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
        </div>
        <p className="text-xs text-gray-500 text-center">Abrechnung sicher über Digistore24 · gleiche Laufzeit wie Ihr Abo</p>
      </DialogContent>
    </Dialog>
  );
};

export default LicenseUpgradeModal;
